import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  MenuItem,
  Select,
  Paper,
  TextField,
  Typography,
  Tooltip,
  useTheme
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

const EditIdCard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme=useTheme()
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  // Static data for candidates
  const staticCandidateData = [
    { candidateId: 1, candidateName: 'John Doe' },
    { candidateId: 2, candidateName: 'Jane Smith' },
    { candidateId: 3, candidateName: 'Alice Johnson' },
    { candidateId: 4, candidateName: 'Bob Brown' },
    { candidateId: 5, candidateName: 'Charlie Davis' },
  ];

  // Static data for ID cards
  const staticIdCardData = [
    { id: 1, studentName: 'John Doe', issueDate: '2023-01-01', expiryDate: '2025-01-01' },
    { id: 2, studentName: 'Jane Smith', issueDate: '2023-02-01', expiryDate: '2025-02-01' },
    { id: 3, studentName: 'Alice Johnson', issueDate: '2023-03-01', expiryDate: '2025-03-01' },
    { id: 4, studentName: 'Bob Brown', issueDate: '2023-04-01', expiryDate: '2025-04-01' },
    { id: 5, studentName: 'Charlie Davis', issueDate: '2023-05-01', expiryDate: '2025-05-01' },
    { id: 6, studentName: 'Diana Evans', issueDate: '2023-06-01', expiryDate: '2025-06-01' },
  ];

  useEffect(() => {
    // Auto-fill the form with the data of the selected ID card
    const idCard = staticIdCardData.find(card => card.id === parseInt(id));
    if (idCard) {
      setCandidateId(staticCandidateData.find(candidate => candidate.candidateName === idCard.studentName)?.candidateId || '');
      setIssueDate(idCard.issueDate);
      setExpiryDate(idCard.expiryDate);
    }
  }, [id]);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateId && issueDate && expiryDate) {
      setShowSuccessModal(true);
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to the previous page
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)',
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '0 auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit ID Card
        </Typography>
      </Box>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <form>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel htmlFor="candidate">Candidate Name:</InputLabel>
            <Select
              id="candidate"
              value={candidateId}
              label="Candidate Name"
              onChange={(e) => setCandidateId(e.target.value)}
            >
              {staticCandidateData.map((candidate) => (
                <MenuItem key={candidate.candidateId} value={candidate.candidateId}>
                  {candidate.candidateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Issue Date"
            type="date"
            variant="outlined"
            fullWidth
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Expiry Date"
            type="date"
            variant="outlined"
            fullWidth
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
           sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Tooltip title={!candidateId ? "Select Candidate" : !issueDate ? "Select Issue Date" : !expiryDate ? "Select Expiry Date" : ""} arrow>
              <span>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    mr: 2,
                    backgroundColor: '#127498ff',
                    '&:hover': { backgroundColor: '#116280ff' }
                  }}
                  disabled={!candidateId || !issueDate || !expiryDate}
                >
                  SAVE
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
            >
              CANCEL
            </Button>
          </Box>
        </form>
      </Paper>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              ID Card Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              ID Card Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditIdCard;
